import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getWeatherService from '../services/getWeatherService';
import getFallbackWeatherUnit from 'owa-weather-option/lib/utils/getFallbackWeatherUnit';
import TemperatureUnit from 'owa-service/lib/contract/TemperatureUnit';
import type WeatherLocationId from 'owa-service/lib/contract/WeatherLocationId';
import weatherStore from '../store';
import WeatherTemperatureUnit from 'owa-service/lib/contract/WeatherTemperatureUnit';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { trace } from 'owa-trace';
import type { WeatherStore } from '../schema/WeatherStore';
import { DatapointVariant } from 'owa-analytics';

export interface PullWeatherState {
    store: WeatherStore;
}

var lastPendingPromise = null;

/**
 * Action to get weather grouped by weather locations
 */
export let pullWeather = createLegacyActionWithDatapoint('pullWeather', {
    name: 'pullWeather',
    options: {
        variant: DatapointVariant.WarmOnly, // Sample these such that only the first request in every session is logged
    },
})(function pullWeather(state: PullWeatherState = { store: weatherStore }): Promise<void> {
    const userWeatherUnit = getUserConfiguration().UserOptions.WeatherUnit;
    var localPromise = getWeatherService(
        convertTemperatureUnit(
            userWeatherUnit === WeatherTemperatureUnit.Default
                ? getFallbackWeatherUnit()
                : userWeatherUnit
        ),
        getWeatherLocationsFromUserOptions()
    );
    lastPendingPromise = localPromise;
    return localPromise
        .then(response => {
            if (localPromise == lastPendingPromise) {
                if (!response.ErrorMessage?.length) {
                    state.store.isValid = true;
                    let weatherLocations = [];
                    response.WeatherLocations.map(location => {
                        // Bug 26257: Sometimes the weather service can return a location entry
                        // with null multi-day forecast and an error message set. In those cases,
                        // ignore the location and log a warning.
                        if (location && !location.ErrorMessage && location.MultidayForecast) {
                            weatherLocations.push(location);
                        } else {
                            let message = location?.ErrorMessage
                                ? location.ErrorMessage
                                : 'No error';
                            trace.warn(
                                `Found invalid weather location, possible workaround is to re-add locations in weather options: ${message}`
                            );
                        }
                    });
                    state.store.weatherLocations = weatherLocations;
                    state.store.pollingWindowInMinutes = response.PollingWindowInMinutes;
                } else {
                    trace.warn(
                        `[pullWeather] getWeatherService is failed: ${response.ErrorMessage}`
                    );
                }
            }
        })
        .catch(error => {
            state.store.isValid = false;
            trace.warn(`[pullWeather] getWeatherService is rejected: ${error}`);
        });
});

/**
 * Gets weather locations from user options
 */
function getWeatherLocationsFromUserOptions(): WeatherLocationId[] {
    let userOptions = getUserConfiguration().UserOptions;
    return userOptions.WeatherLocations
        ? userOptions.WeatherLocations.map(weatherLocation => {
              let latitude = parseFloat(weatherLocation.Latitude);
              let longitude = parseFloat(weatherLocation.Longitude);
              return {
                  EntityId: weatherLocation.LocationId,
                  Name: weatherLocation.Name,
                  Latitude: isNaN(latitude) ? 0 : latitude,
                  Longitude: isNaN(longitude) ? 0 : longitude,
              };
          })
        : [];
}

/**
 * Converts WeatherTemperatureUnit.Celsius/Fahrenheit to TemperatureUnit.Celsius/Fahrenheit respectively
 */
function convertTemperatureUnit(unit: WeatherTemperatureUnit): TemperatureUnit {
    return unit === WeatherTemperatureUnit.Celsius
        ? TemperatureUnit.Celsius
        : TemperatureUnit.Fahrenheit;
}

export default pullWeather;
